import "./style.scss";

import { View } from "@amzn/stencil-react-components/layout";
import {
  ProgressTracker,
  ProgressTrackerLayout,
  ProgressTrackerStep,
  ProgressTrackerStepStatus,
} from "@amzn/stencil-react-components/progress-tracker";
import { useContext } from "react";
import { useIntl } from "react-intl";

import {
  progressRailCompactClassName,
  Step,
  STEP_ID,
} from "@/components/constants";
import { getStepEstimatedMinutes } from "@/helpers/step-time-estimates";
import { AppContext } from "@/stores/appStore";
import { getEnabledSteps } from "@/stores/constants";
import { useFeatureFlagsContext } from "@/stores/contexts/FeatureFlagsContext";

/**
 * `ProgressTracker` requires an `onStepSelected` handler. The rail is rendered with
 * `isStatic`, which strips the click and keyboard handlers, so this is never called.
 */
const noop = (): void => undefined;

/**
 * Message ids for the step labels shown on the rail.
 *
 * These are deliberately separate from each step's `titleId`, which is the heading of the
 * step content itself. The rail uses shorter noun phrases so the labels stay on one or two
 * lines in a narrow column, for example "Identity Verification" on the rail against
 * "Verify your identity" as the step heading.
 *
 * Amazon Account Caching is the exception and reuses its `titleId`. That heading is already
 * the same short noun phrase a rail label would be, so a dedicated resource would only
 * duplicate it and need translating separately.
 */
const RAIL_STEP_LABEL_IDS: Record<STEP_ID, string> = {
  [STEP_ID.IDENTITY_CHECK]: "progressTracker-step-identityCheck",
  [STEP_ID.AMAZON_USERNAME]: "progressTracker-step-amazonUsername",
  [STEP_ID.INSERT_AND_TEST_SECURITY_KEY]:
    "progressTracker-step-insertAndTestSecurityKey",
  [STEP_ID.REGISTER_SECURITY_KEY]: "progressTracker-step-registerSecurityKey",
  [STEP_ID.CONNECT_VPN]: "progressTracker-step-connectVpn",
  [STEP_ID.AMAZON_PASSWORD]: "progressTracker-step-amazonPassword",
  [STEP_ID.PRE_CACHE_AD_MOBILE]: "progressTracker-step-preCacheADMobile",
  [STEP_ID.AMAZON_ACCOUNT_CACHING]: "amazonAccountCaching-expander-title",
};

/**
 * Maps a step onto the status the tracker renders it with.
 *
 * Upcoming steps use `Available` rather than `Disabled` so screen readers announce them
 * as "incomplete" instead of "disabled" — the wizard is linear, so a later step is not
 * unavailable, just not reached yet.
 *
 * @param step The step to map.
 * @param index The index of the step within the enabled steps.
 * @param activeIndex The index of the step the new hire is currently on.
 */
export const getStepStatus = (
  step: Step,
  index: number,
  activeIndex: number
): ProgressTrackerStepStatus => {
  if (step.completed) {
    return ProgressTrackerStepStatus.Complete;
  }
  if (index === activeIndex) {
    return ProgressTrackerStepStatus.Current;
  }
  return ProgressTrackerStepStatus.Available;
};

/**
 * The vertical progress rail shown in the left column of the Day1 onboarding page.
 *
 * Lists every enabled onboarding step with its title and estimated completion time,
 * marking each one complete, current, or upcoming. The rail is informational only —
 * steps are not clickable, because the wizard advances strictly forwards.
 */
export default function ProgressRail(): JSX.Element {
  const intl = useIntl();
  const { state } = useContext(AppContext);
  const { featureFlags } = useFeatureFlagsContext();
  /*
   * Filtered with the same feature flags HomeContent uses. Some steps are gated on a
   * flag, so filtering without them would give the rail a shorter list than the content
   * and the two would disagree on which step currentStepNum refers to.
   */
  const steps = getEnabledSteps(state.steps, featureFlags);
  // currentStepNum is 1 based; it sits past the last step once everything is complete.
  const activeIndex = state.currentStepNum - 1;

  /**
   * An open flyout squeezes the page, so the rail drops its step labels and estimated
   * times to leave the step content a usable width. Opening Get Support also opens the
   * chat support flyout (see the SET_GET_SUPPORT_FLYOUT_VISIBLE reducer case), and the
   * chatbot occupies the same space, so every flyout that overlays the page collapses
   * the rail.
   */
  const isCompact =
    state.chatSupportFlyoutVisible ||
    state.chatbotFlyoutVisible ||
    state.getSupportFlyoutVisible;

  return (
    <View
      className={isCompact ? progressRailCompactClassName : undefined}
      dataTestId="progressRail"
    >
      <ProgressTracker
        aria-label={intl.formatMessage({ id: "progressTracker-ariaLabel" })}
        isStatic={true}
        layout={ProgressTrackerLayout.Vertical}
        onStepSelected={noop}
        selectedStep={activeIndex}
      >
        {steps.map((step, index) => (
          <ProgressTrackerStep
            /*
             * Omitted while compact so the estimated time is not rendered at all. The
             * label has to stay, since it is what the step announces to screen readers,
             * so it is hidden visually in CSS instead.
             */
            descriptionText={
              isCompact
                ? undefined
                : intl.formatMessage(
                    { id: "progressTracker-step-estimatedTime" },
                    { minutes: getStepEstimatedMinutes(step.id) }
                  )
            }
            key={step.id}
            labelText={intl.formatMessage({
              id: RAIL_STEP_LABEL_IDS[step.id],
            })}
            status={getStepStatus(step, index, activeIndex)}
          />
        ))}
      </ProgressTracker>
    </View>
  );
}
